import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type ExcelJS from 'exceljs';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  Activity, AlertCircle, Camera, CheckCircle, ChevronLeft, ChevronRight,
  Download, MapPin, Monitor, RefreshCw, Router, Search, Volume2, WifiOff, X
} from 'lucide-react';
import './HistoricPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3009/api/v1';
const PAGE_SIZE = 20;

// ─── Types ───────────────────────────────────────────────────────────────────

interface DeviceState {
  id: number;
  state: 0 | 1 | null;
  name?: string;
  ipaddress?: string;
}

interface SiteRow {
  id: number;
  name: string;
  address: string;
  active: boolean;
  cameras: DeviceState[];
  routers: DeviceState[];
  sirens: DeviceState[];
  xtam: DeviceState[];
  operativity: number | null;
  service?: { name?: string | null } | null;
  state?: { name?: string | null } | null;
}

interface HistoryRow {
  source: string;
  source_rank: number;
  id: number;
  site_id: number;
  created_at: string;
  device_id: number | null;
  rtsp_ok: boolean | null;
  ping_ok: boolean | null;
  http_ok: boolean | null;
  assumed_ok: boolean;
  code_transaction: string;
  detail_json: unknown | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function deviceOnline(devices: DeviceState[]): { online: number; total: number; hasData: boolean } {
  return {
    online: devices.filter(d => d.state === 1).length,
    total: devices.length,
    hasData: devices.some(d => d.state !== null),
  };
}


function siteStatus(site: SiteRow): 'online' | 'offline' | 'partial' | 'unknown' {
  const all = [...site.cameras, ...site.routers, ...site.sirens, ...site.xtam];
  const known = all.filter(d => d.state !== null);
  if (known.length === 0) return 'unknown';
  const onlineCount = known.filter(d => d.state === 1).length;
  if (onlineCount === known.length) return 'online';
  if (onlineCount === 0) return 'offline';
  return 'partial';
}

function operativityColor(v: number | null): string {
  if (v === null) return '#6b7280';
  if (v >= 80) return '#10B981';
  if (v >= 50) return '#F59E0B';
  return '#EF4444';
}

function fmtTime(d: Date): string {
  return d.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', hour12: false });
}

function fmtDate(d: Date): string {
  return d.toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function todayISO(): string {
  return new Date().toISOString().split('T')[0];
}

function formatSelectedDateLabel(from: string, to: string): string {
  if (from === to) {
    return from === todayISO() ? 'Hoy' : fmtDate(new Date(`${from}T12:00:00`));
  }
  return `${fmtDate(new Date(`${from}T12:00:00`))} → ${fmtDate(new Date(`${to}T12:00:00`))}`;
}

const SITE_STATUS_LABEL: Record<'online' | 'offline' | 'partial' | 'unknown', string> = {
  online: 'Operativo',
  offline: 'Fuera de servicio',
  partial: 'Novedad',
  unknown: 'Sin datos',
};

// ─── Oscilloscope Chart ───────────────────────────────────────────────────────

const LANE_COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#A855F7',
  '#EF4444', '#06B6D4', '#F97316', '#84CC16',
  '#EC4899', '#14B8A6', '#8B5CF6', '#F43F5E',
];

interface OscLane {
  source: string;
  label: string;
  color: string;
  events: { time: number; ok: boolean }[];
}

type TabKey = 'camera' | 'router' | 'siren' | 'xtam_probe';
type DevicesKey = 'cameras' | 'routers' | 'sirens' | 'xtam';

const TAB_DEFS: { key: TabKey; label: string; devicesKey: DevicesKey }[] = [
  { key: 'camera',     label: 'Cámaras', devicesKey: 'cameras' },
  { key: 'router',     label: 'Routers', devicesKey: 'routers' },
  { key: 'siren',      label: 'Sirenas', devicesKey: 'sirens'  },
  { key: 'xtam_probe', label: 'XTam',    devicesKey: 'xtam'    },
];

/** Etiqueta corta por carril (el nombre del sitio ya va en el título del modal). */
const LANE_DEVICE_PREFIX: Record<TabKey, string> = {
  camera: 'Cámara',
  router: 'Router',
  siren: 'Sirena',
  xtam_probe: 'XTam',
};

function buildLanesForSource(
  rows: HistoryRow[],
  source: TabKey,
  currentDevices: DeviceState[],
  timeStart: number,
): OscLane[] {
  const byDevice = new Map<number, { time: number; ok: boolean }[]>();

  // Baseline: inyecta el estado actual al inicio del período para que la gráfica
  // muestre una línea plana cuando no hubo cambios en el rango seleccionado.
  for (const d of currentDevices) {
    if (d.state !== null) {
      byDevice.set(d.id, [{ time: timeStart, ok: d.state === 1 }]);
    }
  }

  // Superpone los eventos reales del historial (transiciones de estado)
  for (const r of rows) {
    if (r.source !== source || r.device_id === null) continue;
    const ok = r.rtsp_ok ?? r.ping_ok ?? r.http_ok;
    if (ok === null || ok === undefined) continue;
    if (!byDevice.has(r.device_id)) byDevice.set(r.device_id, []);
    byDevice.get(r.device_id)!.push({ time: new Date(r.created_at).getTime(), ok });
  }

  const prefix = LANE_DEVICE_PREFIX[source];
  return Array.from(byDevice.entries())
    .sort(([a], [b]) => a - b)
    .map(([deviceId, events], idx) => ({
      source: `${source}_${deviceId}`,
      label: `${prefix} ${idx + 1}`,
      color: LANE_COLORS[idx % LANE_COLORS.length],
      events: events.sort((a, b) => a.time - b.time),
    }));
}

const OscilloscopeChart: React.FC<{
  lanes: OscLane[];
  timeStart: number;
  timeEnd: number;
}> = ({ lanes, timeStart, timeEnd }) => {
  const LANE_H  = 96;
  const LABEL_W = 92;
  const PAD_R   = 16;
  const PAD_TOP = 12;
  const AXIS_H  = 32;
  const SVG_W   = 820;
  const chartW  = SVG_W - LABEL_W - PAD_R;
  const totalH  = lanes.length * LANE_H + PAD_TOP + AXIS_H;
  const span    = Math.max(timeEnd - timeStart, 1);

  const xPx = (t: number) =>
    LABEL_W + Math.max(0, Math.min(1, (t - timeStart) / span)) * chartW;

  const gridTimes: number[] = [];
  const GRID = 6;
  for (let i = 0; i <= GRID; i++) gridTimes.push(timeStart + (i / GRID) * span);

  return (
    <svg
      viewBox={`0 0 ${SVG_W} ${totalH}`}
      style={{ width: '100%', height: 'auto', display: 'block' }}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        {lanes.map(l => (
          <linearGradient key={l.source} id={`hg-${l.source}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={l.color} stopOpacity="0.40" />
            <stop offset="100%" stopColor={l.color} stopOpacity="0.03" />
          </linearGradient>
        ))}
      </defs>

      {/* Chart background */}
      <rect x={LABEL_W} y={PAD_TOP} width={chartW} height={lanes.length * LANE_H}
        fill="#0a0a0a" rx="4" />

      {/* Vertical grid */}
      {gridTimes.map((t, i) => (
        <line key={i}
          x1={xPx(t)} y1={PAD_TOP}
          x2={xPx(t)} y2={PAD_TOP + lanes.length * LANE_H}
          stroke="#374151" strokeWidth="0.5" strokeDasharray="3,3" />
      ))}

      {/* Lanes */}
      {lanes.map((lane, li) => {
        const top  = PAD_TOP + li * LANE_H;
        const yH   = top + LANE_H * 0.20;   // HIGH rail
        const yL   = top + LANE_H * 0.78;   // LOW rail
        const y    = (ok: boolean) => ok ? yH : yL;

        // Build step-function path starting from first event
        let linePath = '';
        let areaPath = '';

        if (lane.events.length > 0) {
          const first = lane.events[0];
          const sx = xPx(first.time);
          const sy = y(first.ok);
          linePath  = `M ${sx} ${sy}`;
          areaPath  = `M ${sx} ${yL} L ${sx} ${sy}`;

          for (let i = 1; i < lane.events.length; i++) {
            const ev = lane.events[i];
            const ex = xPx(ev.time);
            linePath += ` H ${ex} V ${y(ev.ok)}`;
            areaPath += ` H ${ex} V ${y(ev.ok)}`;
          }
          const endX = xPx(timeEnd);
          linePath += ` H ${endX}`;
          areaPath += ` H ${endX} V ${yL} Z`;
        }

        return (
          <g key={lane.source}>
            {/* Lane separator */}
            <line x1={LABEL_W} y1={top + LANE_H} x2={SVG_W - PAD_R} y2={top + LANE_H}
              stroke="#1f2937" strokeWidth="0.5" />

            {/* Label */}
            <text x={LABEL_W - 10} y={top + LANE_H / 2}
              textAnchor="end" dominantBaseline="middle"
              fill={lane.color} fontSize="13" fontFamily="monospace" fontWeight="600">
              {lane.label}
            </text>

            {/* Rail markers */}
            <text x={LABEL_W + 5} y={yH - 3}   fill="#4b5563" fontSize="10" fontFamily="monospace">1</text>
            <text x={LABEL_W + 5} y={yL + 10}  fill="#4b5563" fontSize="10" fontFamily="monospace">0</text>
            <line x1={LABEL_W} y1={yH} x2={SVG_W - PAD_R} y2={yH} stroke="#1f2937" strokeWidth="0.4" />
            <line x1={LABEL_W} y1={yL} x2={SVG_W - PAD_R} y2={yL} stroke="#1f2937" strokeWidth="0.4" />

            {/* Area fill */}
            {areaPath && <path d={areaPath} fill={`url(#hg-${lane.source})`} />}

            {/* Signal */}
            {linePath && (
              <path d={linePath} fill="none" stroke={lane.color}
                strokeWidth="2.2" strokeLinejoin="miter" />
            )}

            {/* Event dots */}
            {lane.events.map((ev, i) => (
              <circle key={i} cx={xPx(ev.time)} cy={y(ev.ok)}
                r="3.5" fill={lane.color} opacity="0.9" />
            ))}
          </g>
        );
      })}

      {/* X-axis time labels */}
      {gridTimes.map((t, i) => (
        <text key={i} x={xPx(t)} y={PAD_TOP + lanes.length * LANE_H + 20}
          textAnchor="middle" fill="#9ca3af" fontSize="11" fontFamily="monospace">
          {fmtTime(new Date(t))}
        </text>
      ))}
    </svg>
  );
};

// ─── Device Counter cell ──────────────────────────────────────────────────────

const DevCount: React.FC<{ online: number; total: number; hasData?: boolean }> = ({ online, total, hasData = true }) => {
  if (total === 0) return <span className="hst-device-none">—</span>;
  const c = !hasData ? '#6b7280'
    : online === total ? '#10B981'
    : online === 0    ? '#EF4444'
    : '#F59E0B';
  return (
    <span className="hst-device-counter" style={{ color: c }}>
      {online}<span className="hst-device-total">/{total}</span>
    </span>
  );
};

const DevBadge: React.FC<{ devices: DeviceState[] }> = ({ devices }) => {
  if (devices.length === 0) return <span className="hst-device-none">—</span>;
  const hasData = devices.some(d => d.state !== null);
  if (!hasData) return (
    <span className="hst-sema hst-sema--unknown">
      <span className="hst-sema__dot" />
      <span>Sin dato</span>
    </span>
  );
  const allOnline = devices.every(d => d.state === 1);
  return (
    <span className={`hst-sema hst-sema--${allOnline ? 'on' : 'off'}`}>
      <span className="hst-sema__dot" />
      <span>{allOnline ? 'ON' : 'OFF'}</span>
    </span>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const HistoricPage: React.FC = () => {
  const { token } = useAuth();
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [sites, setSites]           = useState<SiteRow[]>([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const [search, setSearch]           = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'online' | 'offline' | 'partial' | 'unknown'>('all');
  const [page, setPage]               = useState(1);

  const [dateFrom, setDateFrom] = useState(todayISO());
  const [dateTo, setDateTo]     = useState(todayISO());

  // Modal
  const [selectedSite, setSelectedSite] = useState<SiteRow | null>(null);
  const [historyRows, setHistoryRows]   = useState<HistoryRow[]>([]);
  const [histLoading, setHistLoading]   = useState(false);
  const [histError, setHistError]       = useState<string | null>(null);
  const [activeTab, setActiveTab]       = useState<TabKey>('camera');

  // ── Data fetching ──

  const fetchSites = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_URL}/systemState/sites`, { headers: authHeaders });
      setSites(res.data?.data?.sites ?? []);
      setLastUpdated(new Date());
    } catch (e: any) {
      setError(e.response?.data?.message ?? 'Error al cargar sitios');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { fetchSites(); }, [fetchSites]);

  const fetchHistory = useCallback(async (siteId: number) => {
    setHistLoading(true);
    setHistError(null);
    setHistoryRows([]);
    try {
      const from = new Date(`${dateFrom}T00:00:00`).toISOString();
      const to   = new Date(`${dateTo}T23:59:59`).toISOString();
      const res  = await axios.get(`${API_URL}/systemState/history`, {
        headers: authHeaders,
        params: { siteId, from, to, limit: 2000 },
      });
      setHistoryRows(res.data?.data?.rows ?? []);
    } catch (e: any) {
      setHistError(e.response?.data?.message ?? 'Error al cargar histórico');
    } finally {
      setHistLoading(false);
    }
  }, [authHeaders, dateFrom, dateTo]);

  // ── Modal ──

  const openModal = (site: SiteRow) => {
    setSelectedSite(site);
    const first = TAB_DEFS.find(t => site[t.devicesKey].length > 0);
    setActiveTab(first?.key ?? 'camera');
    fetchHistory(site.id);
  };

  const closeModal = useCallback(() => {
    setSelectedSite(null);
    setHistoryRows([]);
    setHistError(null);
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') closeModal(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [closeModal]);

  // ── Derived data ──

  const filtered = useMemo(() => {
    let list = sites;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(s => s.name.toLowerCase().includes(q) || s.address.toLowerCase().includes(q));
    }
    if (statusFilter !== 'all') {
      list = list.filter(s => siteStatus(s) === statusFilter);
    }
    return list;
  }, [sites, search, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const stats = useMemo(() => {
    let totalCams = 0, onlineCams = 0;
    const onlineSites  = sites.filter(s => siteStatus(s) === 'online').length;
    const partialSites = sites.filter(s => siteStatus(s) === 'partial').length;
    for (const s of sites) {
      const c = deviceOnline(s.cameras);
      totalCams  += c.total;
      onlineCams += c.online;
    }
    const offlineSites = sites.filter(s => siteStatus(s) === 'offline').length;
    const unknownSites = sites.filter(s => siteStatus(s) === 'unknown').length;
    return {
      totalSites: sites.length, onlineSites, partialSites, offlineSites, unknownSites,
      totalCams, onlineCams,
    };
  }, [sites]);

  // ── Chart data ──

  const chartTimeStart = useMemo(() => new Date(`${dateFrom}T00:00:00`).getTime(), [dateFrom]);
  const chartTimeEnd   = useMemo(() => Math.min(new Date(`${dateTo}T23:59:59`).getTime(), Date.now()), [dateTo]);

  const activeLanes = useMemo(() => {
    if (!selectedSite) return [];
    const tab = TAB_DEFS.find(t => t.key === activeTab)!;
    return buildLanesForSource(historyRows, activeTab, selectedSite[tab.devicesKey], chartTimeStart);
  }, [historyRows, activeTab, selectedSite, chartTimeStart]);

  // ── Export Excel ──

  const exportExcel = async () => {
    if (!sites.length) return;
    const ExcelJS = (await import('exceljs')).default;
    const wb = new ExcelJS.Workbook();
    wb.creator = 'XtamPro VMS';
    wb.created = new Date();

    const ws = wb.addWorksheet('Estado del Sistema');

    const HEADER_FILL: ExcelJS.Fill = {
      type: 'pattern', pattern: 'solid',
      fgColor: { argb: 'FFC6E0B4' },
    };
    const BORDER: Partial<ExcelJS.Borders> = {
      top:    { style: 'thin', color: { argb: 'FF9CA3AF' } },
      left:   { style: 'thin', color: { argb: 'FF9CA3AF' } },
      bottom: { style: 'thin', color: { argb: 'FF9CA3AF' } },
      right:  { style: 'thin', color: { argb: 'FF9CA3AF' } },
    };
    const STATUS_LABEL = SITE_STATUS_LABEL;

    ws.columns = [
      { header: 'Sitio',          key: 'name',        width: 32 },
      { header: 'Dirección',      key: 'address',     width: 42 },
      { header: 'Estado',         key: 'status',      width: 15 },
      { header: 'Cámaras Online', key: 'camOnline',   width: 16 },
      { header: 'Cámaras Total',  key: 'camTotal',    width: 15 },
      { header: 'Router Online', key: 'rtOnline',    width: 16 },
      { header: 'Router Total',  key: 'rtTotal',     width: 15 },
      { header: 'Sirena Online', key: 'srOnline',    width: 16 },
      { header: 'Sirena Total',  key: 'srTotal',     width: 15 },
      { header: 'XTam Online',    key: 'xtOnline',    width: 14 },
      { header: 'XTam Total',     key: 'xtTotal',     width: 12 },
      { header: 'Operatividad %', key: 'operativity', width: 15 },
    ];

    const headerRow = ws.getRow(1);
    headerRow.height = 22;
    headerRow.eachCell((cell: ExcelJS.Cell) => {
      cell.fill      = HEADER_FILL;
      cell.border    = BORDER;
      cell.font      = { bold: true, color: { argb: 'FF1F2937' }, size: 11 };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
    });

    for (const s of sites) {
      const c  = deviceOnline(s.cameras);
      const r  = deviceOnline(s.routers);
      const sr = deviceOnline(s.sirens);
      const x  = deviceOnline(s.xtam);
      const row = ws.addRow({
        name:        s.name,
        address:     s.address,
        status:      STATUS_LABEL[siteStatus(s)] ?? siteStatus(s),
        camOnline:   c.online,  camTotal:  c.total,
        rtOnline:    r.online,  rtTotal:   r.total,
        srOnline:    sr.online, srTotal:   sr.total,
        xtOnline:    x.online,  xtTotal:   x.total,
        operativity: s.operativity ?? '',
      });
      row.eachCell({ includeEmpty: true }, (cell: ExcelJS.Cell) => {
        cell.border    = BORDER;
        cell.alignment = { vertical: 'middle' };
      });
    }

    const buf  = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const a   = document.createElement('a');
    a.href    = url;
    a.download = `estado_sistema_${new Date().toISOString().split('T')[0]}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="hst-page">

      {/* ── Header ── */}
      <div className="hst-header">
        <div className="hst-header-top">
          <div>
            <h1 className="hst-title">Estado del Sistema</h1>
            <p className="hst-subtitle">
              {lastUpdated
                ? `Último reporte: ${fmtTime(lastUpdated)} · ${fmtDate(lastUpdated)}`
                : 'Cargando...'}
            </p>
          </div>
          <div className="hst-header-actions">
            <div className="hst-date-range">
              <input type="date" className="hst-date-input"
                value={dateFrom} max={todayISO()}
                onChange={e => { setDateFrom(e.target.value); setPage(1); }} />
              <span className="hst-date-sep">→</span>
              <input type="date" className="hst-date-input"
                value={dateTo} max={todayISO()}
                onChange={e => { setDateTo(e.target.value); setPage(1); }} />
            </div>
            <button className="hst-btn-secondary" onClick={fetchSites} disabled={loading}>
              <RefreshCw size={15} className={loading ? 'hst-spin' : ''} />
              Actualizar
            </button>
            <button className="hst-btn-primary" onClick={exportExcel} disabled={!sites.length}>
              <Download size={15} />
              Exportar Excel
            </button>
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="hst-body">

        {/* Filter Cards */}
        <div className="hst-filter-cards">
          {([
            { key: 'all',     label: 'Todos',         count: stats.totalSites,   icon: <MapPin size={18} />,      color: '#3B82F6', bg: 'rgba(59,130,246,0.12)'  },
            { key: 'online',  label: 'Operativo',         count: stats.onlineSites,  icon: <CheckCircle size={18} />, color: '#10B981', bg: 'rgba(16,185,129,0.12)'  },
            { key: 'partial', label: 'Novedad',           count: stats.partialSites, icon: <AlertCircle size={18} />, color: '#F59E0B', bg: 'rgba(245,158,11,0.12)'  },
            { key: 'offline', label: 'Fuera de servicio', count: stats.offlineSites, icon: <WifiOff size={18} />,     color: '#EF4444', bg: 'rgba(239,68,68,0.12)'   },
          ] as const).map(({ key, label, count, icon, color, bg }) => (
            <button
              key={key}
              className={`hst-filter-card${statusFilter === key ? ' hst-filter-card--active' : ''}`}
              style={{ '--card-color': color, '--card-bg': bg } as React.CSSProperties}
              onClick={() => { setStatusFilter(key); setPage(1); }}
            >
              <div className="hst-filter-card-icon">{icon}</div>
              <div className="hst-filter-card-body">
                <div className="hst-filter-card-count">{count}</div>
                <div className="hst-filter-card-label">{label}</div>
              </div>
              {statusFilter === key && <div className="hst-filter-card-dot" />}
            </button>
          ))}
        </div>

        {/* Info bar */}
        <div className="hst-info-bar">
          <span className="hst-info-pill">
            <Camera size={13} />
            Cámaras&nbsp;
            <strong style={{ color: operativityColor(stats.totalCams ? Math.round(100 * stats.onlineCams / stats.totalCams) : null) }}>
              {stats.onlineCams}/{stats.totalCams}
            </strong>
          </span>
          <span className="hst-info-pill">
            <Activity size={13} />
            <strong>{formatSelectedDateLabel(dateFrom, dateTo)}</strong>
          </span>
        </div>

        {/* Search */}
        <div className="hst-filters">
          <div className="hst-search-wrap">
            <Search size={15} className="hst-search-icon" />
            <input type="text" className="hst-search-input"
              placeholder="Buscar sitio o dirección..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }} />
          </div>
        </div>

        {/* Error banner */}
        {error && (
          <div className="hst-error">
            <AlertCircle size={15} />{error}
          </div>
        )}

        {/* Table */}
        <div className="hst-table-wrap">
          {loading ? (
            <div className="hst-loading">
              <RefreshCw size={22} className="hst-spin" />
              <span>Cargando sitios...</span>
            </div>
          ) : (
            <table className="hst-table">
              <thead>
                <tr>
                  <th>Sitio</th>
                  <th>Estado</th>
                  <th>Cámaras</th>
                  <th>Router</th>
                  <th>Sirena</th>
                  <th>XTam</th>
                  <th>Operatividad</th>
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="hst-table-empty">
                      No se encontraron sitios
                    </td>
                  </tr>
                ) : paginated.map(site => {
                  const st = siteStatus(site);
                  return (
                    <tr key={site.id} className="hst-table-row" onClick={() => openModal(site)}>
                      <td>
                        <div className="hst-site-name">{site.name}</div>
                        <div className="hst-site-addr">{site.address}</div>
                      </td>
                      <td>
                        <span className={`hst-badge hst-badge-${st}`}>
                          {SITE_STATUS_LABEL[st]}
                        </span>
                      </td>
                      <td><DevCount {...deviceOnline(site.cameras)} /></td>
                      <td><DevBadge devices={site.routers} /></td>
                      <td><DevBadge devices={site.sirens}  /></td>
                      <td><DevBadge devices={site.xtam}    /></td>
                      <td>
                        <div className="hst-op-cell">
                          <div className="hst-op-track">
                            <div className="hst-op-fill"
                              style={{
                                width: `${site.operativity ?? 0}%`,
                                background: operativityColor(site.operativity),
                              }} />
                          </div>
                          <span className="hst-op-pct"
                            style={{ color: operativityColor(site.operativity) }}>
                            {site.operativity !== null ? `${site.operativity}%` : '—'}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="hst-pagination">
            <span className="hst-pg-info">
              {filtered.length} sitios · Página {page} de {totalPages}
            </span>
            <div className="hst-pg-btns">
              <button className="hst-pg-btn"
                onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                <ChevronLeft size={15} />
              </button>
              <button className="hst-pg-btn"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                <ChevronRight size={15} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Modal ── */}
      {selectedSite && (
        <div className="hst-overlay" onClick={closeModal}>
          <div className="hst-modal" onClick={e => e.stopPropagation()}>

            {/* Modal header */}
            <div className="hst-modal-header">
              <div>
                <div className="hst-modal-title">
                  <MapPin size={17} color="#A61B34" />
                  {selectedSite.name}
                </div>
                <div className="hst-modal-sub">{selectedSite.address}</div>
              </div>
              <button className="hst-modal-close" onClick={closeModal}>
                <X size={17} />
              </button>
            </div>

            {/* Device summary row */}
            <div className="hst-modal-summary">
              {/* Cámaras: contador numérico */}
              {(() => {
                const c = deviceOnline(selectedSite.cameras);
                return (
                  <div className="hst-modal-dev">
                    <span className="hst-modal-dev-icon"><Camera size={13} /></span>
                    <span className="hst-modal-dev-lbl">Cámaras</span>
                    <span className="hst-modal-dev-cnt" style={{
                      color: c.total === 0 ? '#6b7280'
                           : !c.hasData    ? '#6b7280'
                           : c.online === c.total ? '#10B981'
                           : c.online === 0 ? '#EF4444' : '#F59E0B',
                    }}>
                      {c.total === 0 ? '—' : `${c.online}/${c.total}`}
                    </span>
                  </div>
                );
              })()}
              {/* Router / Sirena / XTam: badge ON/OFF */}
              {([
                { label: 'Router',  devices: selectedSite.routers, icon: <Router  size={13} /> },
                { label: 'Sirena',  devices: selectedSite.sirens,  icon: <Volume2 size={13} /> },
                { label: 'XTam',    devices: selectedSite.xtam,    icon: <Monitor size={13} /> },
              ] as const).map(({ label, devices, icon }) => (
                <div key={label} className="hst-modal-dev">
                  <span className="hst-modal-dev-icon">{icon}</span>
                  <span className="hst-modal-dev-lbl">{label}</span>
                  <DevBadge devices={devices as DeviceState[]} />
                </div>
              ))}
              <div className="hst-modal-dev">
                <span className="hst-modal-dev-icon"><Activity size={13} /></span>
                <span className="hst-modal-dev-lbl">Operatividad</span>
                <span className="hst-modal-dev-cnt"
                  style={{ color: operativityColor(selectedSite.operativity) }}>
                  {selectedSite.operativity !== null ? `${selectedSite.operativity}%` : '—'}
                </span>
              </div>
            </div>

            {/* Tab buttons */}
            <div className="hst-modal-tabs">
              {TAB_DEFS.map(tab => {
                const count = selectedSite[tab.devicesKey].length;
                return (
                  <button
                    key={tab.key}
                    className={`hst-modal-tab${activeTab === tab.key ? ' hst-modal-tab--active' : ''}${count === 0 ? ' hst-modal-tab--empty' : ''}`}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    {tab.label}
                    <span className="hst-modal-tab-badge">{count}</span>
                  </button>
                );
              })}
              <span className="hst-modal-tab-period">
                {dateFrom === dateTo
                  ? fmtDate(new Date(dateFrom + 'T12:00:00'))
                  : `${dateFrom} → ${dateTo}`}
              </span>
            </div>

            {/* Chart area — altura fija en modal, scroll interno, animación por tab */}
            <div className="hst-modal-chart">
              {histLoading ? (
                <div className="hst-loading">
                  <RefreshCw size={20} className="hst-spin" />
                  <span>Cargando histórico...</span>
                </div>
              ) : histError ? (
                <div className="hst-error"><AlertCircle size={15} />{histError}</div>
              ) : (
                <div key={activeTab} className="hst-tab-panel">
                  {activeLanes.length === 0 ? (
                    <div className="hst-chart-empty">
                      <WifiOff size={30} color="#374151" />
                      <p>
                        {selectedSite[TAB_DEFS.find(t => t.key === activeTab)!.devicesKey].length === 0
                          ? 'Este sitio no tiene dispositivos de este tipo'
                          : 'Sin registros en el período seleccionado'}
                      </p>
                    </div>
                  ) : (
                    <>
                      <OscilloscopeChart
                        lanes={activeLanes}
                        timeStart={chartTimeStart}
                        timeEnd={chartTimeEnd}
                      />
                      <div className="hst-legend">
                        {activeLanes.map(l => (
                          <span key={l.source} className="hst-legend-item">
                            <span className="hst-legend-dot" style={{ background: l.color }} />
                            {l.label}
                          </span>
                        ))}
                      </div>
                      <div className="hst-chart-meta">
                        {activeLanes.length} dispositivo{activeLanes.length !== 1 ? 's' : ''} · {historyRows.length} eventos totales
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HistoricPage;
